import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator,
  FileText, 
  CheckCircle2, 
  Settings, 
  Star,
  Shield,
  MessageSquare,
  FileCheck,
  Clipboard,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Quote,
  X,
  Coins,
  Users,
  TrendingUp,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FeatureCard from '../components/home/FeatureCard';

// Configuration des témoignages
const testimonials = [
  {
    quote: "Grâce au devis détaillé, j'ai économisé 12 000€ sur mes travaux de rénovation. Les artisans ont revu leurs prix quand je leur ai montré l'estimation détaillée de SimulReno.",
    author: "Jean B.",
    location: "Lyon",
    savings: "12 000€",
    type: "Rénovation complète"
  },
  {
    quote: "Mon prêt travaux a été accepté en une semaine ! La banque a apprécié le niveau de détail du devis SimulReno et sa crédibilité.",
    author: "Sophie M.",
    location: "Strasbourg",
    project: "Financement obtenu : 45 000€",
    type: "Extension maison"
  },
  {
    quote: "J'ai comparé 5 devis d'artisans avec l'estimation SimulReno. Résultat : 3 500€ d'économies sur ma salle de bain en négociant les prix excessifs.",
    author: "Marc D.",
    location: "Nantes",
    savings: "3 500€",
    type: "Rénovation salle de bain"
  },
  {
    quote: "L'estimation détaillée m'a permis d'identifier un devis gonflé sur l'électricité. En le montrant à l'artisan, il a baissé son prix de 1 200€ !",
    author: "Laura P.",
    location: "Bordeaux",
    savings: "1 200€",
    type: "Rénovation électrique"
  },
  {
    quote: "Mon dossier de prêt était initialement refusé. Avec le devis SimulReno, la banque a validé mon financement de 85 000€ pour mes travaux.",
    author: "Thomas R.",
    location: "Lille",
    project: "Financement obtenu : 85 000€",
    type: "Rénovation énergétique"
  }
];

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [isAnimating]);

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
      );
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <div className="relative overflow-hidden p-8">
      <div className="relative w-full">
        <div className={`transition-transform duration-500 ease-in-out transform ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="flex justify-center mb-6">
              <Quote className="h-12 w-12 text-primary/20" />
            </div>
            <blockquote className="text-xl text-center italic mb-6">
              {testimonials[currentIndex].quote}
            </blockquote>
            <div className="text-center">
              <p className="font-semibold text-primary">
                {testimonials[currentIndex].author}, {testimonials[currentIndex].location}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {testimonials[currentIndex].type}
              </p>
              {testimonials[currentIndex].savings && (
                <div className="mt-4">
                  <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                    Économie réalisée : {testimonials[currentIndex].savings}
                  </span>
                </div>
              )}
              {testimonials[currentIndex].project && (
                <div className="mt-4">
                  <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">
                    {testimonials[currentIndex].project}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-primary p-2 rounded-full shadow-lg"
        aria-label="Témoignage précédent"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-primary p-2 rounded-full shadow-lg"
        aria-label="Témoignage suivant"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`h-2 w-2 rounded-full transition-colors ${
              index === currentIndex ? 'bg-primary' : 'bg-gray-300'
            }`}
            onClick={() => {
              setIsAnimating(true);
              setCurrentIndex(index);
              setTimeout(() => setIsAnimating(false), 500);
            }}
            aria-label={`Aller au témoignage ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Composant pour le bouton d'estimation sticky sur mobile
const MobileStickyCTA = () => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 sm:hidden z-50 mobile-sticky-banner">
      <Link
        to="/quick-estimate"
        className="btn bg-primary text-white hover:bg-primary/90 flex items-center justify-center w-full relative"
      >
        <Calculator className="h-6 w-6 mr-2" />
        Démarrez une estimation rapide
        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">GRATUIT</span>
      </Link>
      <button 
        className="absolute -top-3 right-3 bg-white rounded-full p-1 shadow-md"
        onClick={() => setIsVisible(false)}
        aria-label="Fermer la bannière"
      >
        <X className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  );
};

// Calculateur d'économies potentielles
const SavingsCalculator = () => {
  const [projectAmount, setProjectAmount] = useState(10000);
  const estimatedSavings = Math.round(projectAmount * 0.13);
  const roi = Math.round(estimatedSavings / 49.9);

  return (
    <div className="bg-gray-50 p-6 rounded-lg mb-8 shadow-sm">
      <p className="font-medium mb-4 text-lg">Calculez votre économie potentielle :</p>
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
        <div className="flex items-center">
          <span className="mr-2">Budget travaux estimé :</span>
          <select 
            value={projectAmount} 
            onChange={(e) => setProjectAmount(Number(e.target.value))}
            className="p-2 rounded border"
            aria-label="Sélectionnez votre budget de travaux"
          >
            <option value="5000">5 000 €</option>
            <option value="10000">10 000 €</option>
            <option value="20000">20 000 €</option>
            <option value="50000">50 000 €</option>
            <option value="100000">100 000 €</option>
          </select>
        </div>
        <div className="bg-green-100 text-green-800 p-3 rounded-lg font-medium flex-1 text-center">
          Économie potentielle : <span className="font-bold text-xl">{estimatedSavings} €</span>
        </div>
      </div>
      <p className="text-sm text-gray-700">
        Soit un retour sur investissement de <span className="font-bold text-green-600">{roi}x</span> votre achat de crédit !
      </p>
    </div>
  );
};
const Home = () => {
  const { user } = useAuth();
  const detailedEstimatePath = user ? '/estimation/nouvelle' : '/register';

  return (
    <div className="bg-white text-gray-700">
      {/* Hero Section with Background Image */}
      <div className="relative">
        {/* Fond d'image avec gradient violet */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(48, 25, 97, 0.3), rgba(48, 25, 97, 0.9)), url(/hero-renovation.jpg)',
            minHeight: '600px'
          }}
          role="img"
          aria-label="Image de fond représentant une rénovation"
        ></div>
        
        {/* Overlay flou sur le côté gauche pour améliorer la lisibilité */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(48, 25, 97, 0.4) 0%, transparent 70%)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(3px)',
            clipPath: 'polygon(0 0, 60% 0, 60% 100%, 0 100%)',
            minHeight: '600px'
          }}
        ></div>
        
        {/* Contenu */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24" style={{minHeight: '600px'}}>
          <div className="max-w-5xl">
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Rénover, oui ! Mais pas à n'importe quel prix !<br />
              Evaluez votre budget travaux en 5 minutes.
            </h1>
           <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Rapide. Factuel. Aux prix réels 2025.<br />
              <strong>Deux options :</strong> 
              <span className="text-white px-2 py-1 rounded">Estimation rapide <strong>gratuite</strong></span> ou 
              <span className="text-white px-2 py-1 rounded ml-2">Devis détaillé avec <strong>crédits</strong> (49,90€)</span><br />
              De quoi être rassuré(e) et prendre les bonnes décisions... sans se faire avoir.
              <br /><br />
              SimulReno n'est pas une entreprise de travaux. C'est est un outil en ligne indépendant qui calcule le coût réel des matériaux + main d'oeuvre, pour plus de 250 types de travaux.
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <Link
                to="/quick-estimate"
                className="btn bg-white text-primary hover:bg-gray-100 inline-flex items-center justify-center text-lg relative"
              >
                <Calculator className="h-5 w-5 mr-2" />
                Démarrez une estimation rapide
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">GRATUIT</span>
              </Link>
              {!user && (
                <Link
                  to="/register"
                  className="text-white/90 hover:text-white underline text-lg sm:self-center sm:ml-4"
                >
                  Créer un compte gratuitement
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Banner */}
      <div className="bg-gray-50 py-4 border-t border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
            <div className="flex items-center">
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="ml-2 font-medium">4.8/5 sur 293 avis</span>
            </div>
            <div className="flex items-center">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="ml-2 font-medium">300+ utilisateurs satisfaits</span>
            </div>
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <span className="ml-2 font-medium">13% d'économies en moyenne</span>
            </div>
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-primary" />
              <span className="ml-2 font-medium">Satisfait ou remboursé</span>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-primary mb-12 text-center">
            Pourquoi nos estimations font la différence ?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<Shield className="h-6 w-6" />}
              title="Indépendance totale"
              description="0 artisan partenaire. Nos estimations sont 100 % impartiales et basées sur des données réelles."
            />
            <FeatureCard
              icon={<Settings className="h-6 w-6" />}
              title="Juste prix garanti"
              description="Chaque enveloppe travaux est calculée pour refléter le coût actuel du marché. Redoutable pour négocier en toute confiance."
            />
            <FeatureCard
              icon={<Star className="h-6 w-6" />}
              title="Expertise prouvée"
              description="Nos chiffres s'appuient sur + de 300 devis passés au crible par notre équipe d'experts."
            />
            <FeatureCard
              icon={<CheckCircle2 className="h-6 w-6" />}
              title="Un outil de négociation"
              description="Nos utilisateurs gagnent en moyenne 13% sur leur facture finale. Ca vaut le coup, non ?"
            />
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="absolute -top-4 -right-4 bg-orange-500 text-white text-sm font-bold py-1 px-3 rounded-full shadow-lg transform rotate-12 z-10 md:block hidden">
            NOUVEAU
          </div>
          <h2 className="text-3xl font-bold text-primary mb-12 text-center">
            Comment ça marche ?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Free Option */}
            <div className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-green-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white font-bold py-1 px-4 rounded-full text-sm">
                GRATUIT
              </div>
              <h3 className="text-2xl font-bold text-center mb-6">Estimation Rapide</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <Calculator className="h-6 w-6 text-green-500 mr-3 mt-1" />
                  <p>Estimation globale en 5 minutes</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 mt-1" />
                  <p>Vision macroscopique du budget</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 mt-1" />
                  <p>Idéal pour une première approche</p>
                </div>
                <div className="flex items-start opacity-50">
                  <X className="h-6 w-6 text-red-500 mr-3 mt-1" />
                  <p>Sans détail poste par poste</p>
                </div>
                <div className="flex items-start opacity-50">
                  <X className="h-6 w-6 text-red-500 mr-3 mt-1" />
                  <p>Pas d'export PDF professionnel</p>
                </div>
              </div>
              <Link
                to="/quick-estimate"
                className="btn bg-green-500 hover:bg-green-600 text-white w-full"
              >
                Démarrer gratuitement
              </Link>
            </div>
            
            {/* Paid Option */}
            <div className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-primary relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white font-bold py-1 px-4 rounded-full text-sm">
                49,90€/ADRESSE
              </div>
              <div className="absolute -top-4 right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                CHOIX POPULAIRE
              </div>
              <h3 className="text-2xl font-bold text-center mb-6">Estimation Détaillée</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <Coins className="h-6 w-6 text-primary mr-3 mt-1" />
                  <p><strong>1 crédit = 1 adresse</strong> à vie avec estimations illimitées</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-primary mr-3 mt-1" />
                  <p>Devis détaillé avec +250 types de travaux</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-primary mr-3 mt-1" />
                  <p>Export PDF pour dossier bancaire</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-primary mr-3 mt-1" />
                  <p>Économies de 13% en moyenne sur votre facture finale</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-primary mr-3 mt-1" />
                  <p>Variations illimitées pour une même adresse</p>
                </div>
              </div>
              <Link
                to="/credits/purchase"
                className="btn bg-primary hover:bg-primary/90 text-white w-full transition-transform duration-300 hover:scale-105"
              >
                Acheter un crédit (49,90€)
              </Link>
            </div>
          </div>
          
          <SavingsCalculator />

          <div className="mt-8 bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
            <p className="text-orange-800">
              <span className="font-bold">Un investissement rentable :</span> En moyenne, nos utilisateurs économisent 13% sur leur facture finale en utilisant notre estimation détaillée comme base de négociation avec les artisans. Pour un projet de 10 000€, c'est un gain potentiel de 1 300€ !
            </p>
          </div>
        </div>
      </div>
      {/* Comparative Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-primary mb-12 text-center">
            Pourquoi choisir <span className="text-orange-500">SimulReno</span> plutôt que demander un devis à un artisan ?
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg border border-gray-200 shadow-lg text-sm">
              <thead className="bg-primary/5 text-primary font-semibold">
                <tr>
                  <th className="text-left px-6 py-4 rounded-tl-lg">Critère</th>
                  <th className="text-left px-6 py-4">Devis artisan</th>
                  <th className="text-left px-6 py-4 rounded-tr-lg">SimulReno</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-6 py-4 font-medium">Temps d'attente</td>
                  <td className="px-6 py-4 text-red-600">Plusieurs jours à plusieurs semaines</td>
                  <td className="px-6 py-4 font-medium text-green-600">1 à 30 minutes</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Tarif pour obtenir le devis</td>
                  <td className="px-6 py-4 text-red-600">Souvent 50 € à 150 €</td>
                  <td className="px-6 py-4 font-medium text-green-600">0 € (rapide) ou 49,90 € (détaillé)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Couverture des postes</td>
                  <td className="px-6 py-4 text-red-600">Partielle ou orientée "prestataire"</td>
                  <td className="px-6 py-4 font-medium text-green-600">+250 types de travaux, coûts cachés inclus</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Neutralité</td>
                  <td className="px-6 py-4 text-red-600">Lié à ses intérêts de vente</td>
                  <td className="px-6 py-4 font-medium text-green-600">Totalement indépendant</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Valeur ajoutée</td>
                  <td className="px-6 py-4 text-red-600">Devis à lire seul</td>
                  <td className="px-6 py-4 font-medium text-green-600">Base de négociation + outil de décision</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Retour sur investissement</td>
                  <td className="px-6 py-4 text-red-600">Aucun</td>
                  <td className="px-6 py-4 font-medium text-green-600">13% d'économies en moyenne (soit 1 300€ pour 10 000€ de travaux)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
            <p className="text-orange-800">
              <span className="font-bold">Et en plus :</span> Votre estimation prend en compte automatiquement les contraintes techniques (échafaudage, hauteur sous plafond, humidité, etc.), comme le ferait un vrai professionnel.
            </p>
          </div>

          <div className="text-center mt-12">
            <Link
              to={detailedEstimatePath}
              className="btn bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center text-lg px-8 transition-transform duration-300 hover:scale-105"
            >
              Passer à l'estimation détaillée
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <p className="text-gray-500 mt-3">
              Recommandé pour éviter les mauvaises surprises
            </p>
          </div>
        </div>
      </div>

      {/* Detailed Estimation Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-primary mb-6">
                Une estimation ultra-détaillée
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Configurez jusqu'à plus de 250 types de travaux pour obtenir une estimation précise de votre chantier de rénovation. Notre interface intuitive vous guide à travers chaque étape, des études préalables jusqu'aux finitions.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { id: 'description', text: 'Description du bien' },
                  { id: 'etudes', text: 'Études & diagnostics' },
                  { id: 'demolition', text: 'Démolition' },
                  { id: 'gros-oeuvre', text: 'Gros-œuvre' },
                  { id: 'vrd', text: 'VRD' },
                  { id: 'isolation-etancheite', text: <strong>Isolation & étanchéité</strong> },
                  { id: 'cloisons', text: 'Cloisons & aménagements' },
                  { id: 'electricite', text: 'Électricité' },
                  { id: 'sanitaire', text: 'Sanitaire & hygiène' },
                  { id: 'revetements', text: 'Revêtements' },
                  { id: 'portes-fenetres', text: <strong>Portes & fenêtres</strong> },
                  { id: 'finitions', text: 'Finitions' },
                  { id: 'exterieurs', text: 'Extérieurs' },
                  { id: 'resultats', text: 'Résultats' }
                ].map((step) => (
                  <div key={step.id} className="flex items-start space-x-2">
                    <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-sm">{step.text}</span>
                  </div>
                ))}
              </div>
              <Link
                to={detailedEstimatePath}
                className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center transition-transform duration-300 hover:scale-105"
              >
                <Coins className="h-5 w-5 mr-2" />
                Démarrer mon estimation détaillée
                <ArrowRight className="h-6 w-6 ml-2" />
              </Link>
              <p className="text-sm text-gray-500 mt-2">
                49,90€/adresse - Satisfaction garantie ou remboursé
              </p>
            </div>
            <div className="relative">
              <img 
                src="/detailed-steps.png"
                alt="Interface de l'estimation détaillée" 
                className="rounded-lg shadow-lg"
                loading="lazy"
              />
              <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                250+ options de travaux
              </div>
            </div>
          </div>
        </div>
      </div>

     {/* PDF Export Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <img 
                src="/detailed-result.png"
                alt="Export PDF du devis" 
                className="rounded-lg shadow-lg"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Format professionnel
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl font-bold text-primary mb-6">
                Un devis professionnel pour votre dossier
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Exportez votre estimation au format PDF pour :
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <FileCheck className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium">Dossier bancaire</h4>
                    <p className="text-gray-600">Un document détaillé et professionnel pour votre demande de prêt</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clipboard className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium">Négociation avec les artisans</h4>
                    <p className="text-gray-600">Comparez les devis et négociez en toute confiance</p>
                  </div>
                </div>
              </div>
              <Link
                to={detailedEstimatePath}
                className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center transition-transform duration-300 hover:scale-105"
              >
                <Coins className="h-5 w-5 mr-2" />
                Créer mon premier devis détaillé
                <ArrowRight className="h-6 w-6 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-primary mb-6 text-center">
            Ils nous font confiance
          </h2>
          <p className="text-center text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Découvrez comment nos utilisateurs ont économisé sur leurs travaux et obtenu leurs financements grâce à nos estimations détaillées.
          </p>
          <div className="max-w-4xl mx-auto">
            <TestimonialCarousel />
          </div>
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-8">Comparez nos options en un coup d'œil</h2>
          <p className="text-center text-gray-600 mb-8">
            Choisissez l'option qui correspond le mieux à vos besoins et à votre budget
          </p>
          
          <div className="overflow-x-auto mb-8">
            <table className="w-full bg-white rounded-lg shadow-md border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-4 text-left border-b">Fonctionnalités</th>
                  <th className="p-4 text-center border-b">
                    <span className="block text-green-500 font-bold">Estimation rapide</span>
                    <span className="text-xs font-normal">GRATUIT</span>
                  </th>
                  <th className="p-4 text-center border-b">
                    <span className="block text-primary font-bold">Estimation détaillée</span>
                    <span className="text-xs font-normal">49,90€/adresse</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border-b">Vision globale du budget</td>
                  <td className="p-3 border-b text-center">✓</td>
                  <td className="p-3 border-b text-center">✓</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Temps d'obtention</td>
                  <td className="p-3 border-b text-center">2 minutes</td>
                  <td className="p-3 border-b text-center">30 minutes</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Détail poste par poste</td>
                  <td className="p-3 border-b text-center">✗</td>
                  <td className="p-3 border-b text-center">✓</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Personnalisation des travaux</td>
                  <td className="p-3 border-b text-center">Basique</td>
                  <td className="p-3 border-b text-center">Avancée (250+ options)</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Export PDF professionnel</td>
                  <td className="p-3 border-b text-center">✗</td>
                  <td className="p-3 border-b text-center">✓</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Idéal pour dossier bancaire</td>
                  <td className="p-3 border-b text-center">✗</td>
                  <td className="p-3 border-b text-center">✓</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Estimation par adresse</td>
                  <td className="p-3 border-b text-center">Limitée</td>
                  <td className="p-3 border-b text-center">Illimitée à vie</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-8 text-center">
            <Link
              to="/credits/purchase"
              className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center text-lg transition-transform duration-300 hover:scale-105"
            >
              <Coins className="h-5 w-5 mr-2" />
              Obtenir une estimation détaillée
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <p className="mt-3 text-sm text-gray-500">
              49,90€/adresse - Satisfaction garantie ou remboursé
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-primary mb-6 text-center">
            Questions fréquentes
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Nous avons regroupé les questions les plus fréquentes pour vous aider à comprendre notre service
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center text-primary">
                <FileText className="h-5 w-5 mr-2" /> 
                À propos des estimations
              </h3>
              <div className="space-y-4">
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Comment sont calculés les prix ?</summary>
                  <p className="mt-4 text-gray-600">
                    Nos prix sont basés sur une analyse approfondie de plus de 300 devis réels, régulièrement mis à jour. Nous prenons en compte les coûts des matériaux, la main d'œuvre, et les spécificités régionales pour vous donner une estimation au plus proche de la réalité du marché.
                  </p>
                </details>
                
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Et si mon projet est atypique ?</summary>
                  <p className="mt-4 text-gray-600">
                    Notre algorithme prend en compte de nombreux paramètres pour s'adapter à la spécificité de votre projet. Pour les cas très particuliers, nous vous conseillons d'utiliser notre estimation comme base de négociation et d'ajouter une marge de sécurité de 15%.
                  </p>
                </details>
                
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Les prix sont-ils à jour ?</summary>
                  <p className="mt-4 text-gray-600">
                    Oui, notre base de données de prix est mise à jour mensuellement pour refléter les évolutions du marché. Nous prenons en compte l'inflation des matériaux et les variations régionales.
                  </p>
                </details>
                
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Puis-je utiliser l'estimation pour mon prêt ?</summary>
                  <p className="mt-4 text-gray-600">
                    Absolument ! Notre estimation détaillée génère un document PDF professionnel qui est régulièrement accepté par les banques pour les demandes de prêt travaux. Le niveau de détail et la crédibilité de nos calculs sont particulièrement appréciés.
                  </p>
                </details>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center text-primary">
                <Coins className="h-5 w-5 mr-2" /> 
                À propos des crédits
              </h3>
              <div className="space-y-4">
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Comment fonctionnent les crédits SimulReno ?</summary>
                  <p className="mt-4 text-gray-600">
                    Un crédit SimulReno vous donne accès à une estimation détaillée pour une adresse spécifique. Une fois acheté, ce crédit vous permet de créer un nombre illimité d'estimations pour cette même adresse. Vous pouvez ainsi comparer différentes options et variantes de votre projet sans frais supplémentaires.
                  </p>
                </details>
                
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Quelle est la différence entre l'estimation gratuite et l'estimation avec crédits ?</summary>
                  <p className="mt-4 text-gray-600">
                    L'estimation gratuite vous donne une vision globale de votre budget en quelques minutes. L'estimation détaillée (49,90€) vous offre un devis complet poste par poste, un document PDF professionnel pour votre banque, et un outil puissant pour négocier avec les artisans, permettant en moyenne 13% d'économies sur vos travaux.
                  </p>
                </details>

                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Combien de temps est valable un crédit ?</summary>
                  <p className="mt-4 text-gray-600">
                    Un crédit est valable à vie pour l'adresse que vous avez sélectionnée. Vous pouvez revenir modifier votre estimation, créer de nouvelles variantes ou mettre à jour votre projet quand vous le souhaitez, sans limite de temps.
                  </p>
                </details>
                
                <details className="bg-gray-50 p-5 rounded-lg shadow-sm group transition-all duration-200 hover:shadow-md">
                  <summary className="font-semibold cursor-pointer">Comment se fait le remboursement ?</summary>
                  <p className="mt-4 text-gray-600">
                    Si vous n'êtes pas satisfait de votre estimation détaillée, nous vous remboursons intégralement dans les 14 jours suivant votre achat, sans condition. Il suffit de nous contacter via le formulaire de contact.
                  </p>
                </details>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link
              to="/credits/purchase"
              className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center text-lg transition-transform duration-300 hover:scale-105"
            >
              <Coins className="h-5 w-5 mr-2" />
              Obtenir une estimation détaillée
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>

      {/* Call-to-Action Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6 text-primary">
            Prêt à reprendre le contrôle de vos travaux ?
          </h2>
          
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Rejoignez plus de 300 utilisateurs qui ont déjà économisé sur leurs travaux de rénovation grâce à nos estimations précises et indépendantes.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-green-600">Estimation rapide</h3>
              <p className="mb-4 text-gray-600">Vision globale de votre budget travaux en seulement 5 minutes.</p>
              <div className="font-bold text-2xl mb-4 text-gray-700">0€</div>
              <Link
                to="/quick-estimate"
                className="btn bg-green-500 text-white hover:bg-green-600 inline-flex items-center justify-center w-full"
              >
                <Calculator className="h-5 w-5 mr-2" />
                Démarrer gratuitement
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-2 border-primary">
              <div className="absolute top-0 right-4 transform -translate-y-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">
                RECOMMANDÉ
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Estimation détaillée</h3>
              <p className="mb-4 text-gray-600">Devis complet, exportable et utilisable avec votre banque ou artisan.</p>
              <div className="font-bold text-2xl mb-4 text-gray-700">49,90€<span className="text-sm font-normal">/adresse</span></div>
              <Link
                to="/credits/purchase"
                className="btn bg-primary hover:bg-primary/90 text-white inline-flex items-center justify-center w-full transition-transform duration-300 hover:scale-105"
              >
                <Coins className="h-5 w-5 mr-2" />
                Acheter un crédit
              </Link>
            </div>
          </div>
          
          {!user && (
            <div className="mt-6">
              <Link
                to="/register"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Créer un compte gratuitement →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Visual Testimonial Banner */}
      <div className="bg-gray-800 py-12 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center items-center -mx-2">
            <div className="px-2 mb-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
                <Star className="h-5 w-5 text-yellow-400 mr-2" />
                <span className="font-medium">4.8/5</span>
              </div>
            </div>
            <div className="px-2 mb-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <span className="font-medium">+300 utilisateurs</span>
              </div>
            </div>
            <div className="px-2 mb-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <span className="font-medium text-green-400">+500 000€ économisés</span>
              </div>
            </div>
            <div className="px-2 mb-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <span className="font-medium text-blue-400">+700 crédits actifs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Sticky CTA */}
      <MobileStickyCTA />
    </div>
  );
};

export default Home;